// LessonController — drives the real engine + Easy bots through a scripted
// LessonScript, gating the human's actions and blocking wrong moves WITHOUT
// mutating engine state (a rejected attempt leaves the state object identical by
// reference). Pure + synchronous: bots and claims settle immediately (free
// play's timed delay is the BotDriver's concern), so it is unit-testable without
// a canvas. Bots always play EASY_STRATEGY around the learner.

import {
  canTsumo,
  claimsFor,
  declareTsumo,
  discardTile,
  passClaims,
  type MahjongState,
} from '../game/GameState.js';
import { resolveClaimPriority, type ChiOption, type Claim } from '../game/Claims.js';
import type { Seat } from '../game/types.js';
import type { AiStrategy } from '../ai/types.js';
import { EASY_STRATEGY } from '../ai/EasyStrategy.js';
import { mulberry32, type Rng } from '../utils/prng.js';
import { LOCAL_SEAT } from '../scenes/Bootstrap.js';
import { applyWinner, buildClaim, buildLessonState } from './lessonEngine.js';
import type {
  AttemptResult,
  ClaimKind,
  LessonProgress,
  LessonScript,
  LessonStep,
} from './types.js';

/** Fixed noise seed so bot decisions replay identically every run. */
const DEFAULT_LESSON_RNG_SEED = 1;
export class LessonController {
  private state: MahjongState;
  private index = 0;
  private actionDone = false;
  private rejectionKey: string | null = null;
  private pendingBotClaims: readonly Claim[] = [];
  private completed = false;

  constructor(
    private readonly script: LessonScript,
    private readonly strategy: AiStrategy = EASY_STRATEGY,
    private readonly rng: Rng = mulberry32(DEFAULT_LESSON_RNG_SEED),
  ) {
    this.state = buildLessonState(script);
    this.settleBots();
  }

  getState(): MahjongState {
    return this.state;
  }
  stepCount(): number {
    return this.script.steps.length;
  }
  isComplete(): boolean {
    return this.completed;
  }
  currentStep(): LessonStep {
    const step = this.script.steps[this.index];
    if (step === undefined) throw new Error(`lesson step ${this.index} out of range`);
    return step;
  }

  progress(): LessonProgress {
    const satisfied =
      this.completed || this.currentStep().expectedAction.kind === 'info' || this.actionDone;
    return { stepIndex: this.index, satisfied, rejectionKey: this.rejectionKey };
  }

  /** Whether a "Next" tap is meaningful — info + satisfied non-auto steps wait on it. */
  canAdvance(): boolean {
    if (this.completed) return false;
    if (this.currentStep().expectedAction.kind === 'info') return true;
    return this.actionDone;
  }

  /** Manual "Next": moves off an info or satisfied step (completes on the last). */
  advance(): void {
    const step = this.currentStep();
    if (step.expectedAction.kind !== 'info' && !this.actionDone) return;
    if (this.index >= this.script.steps.length - 1) {
      this.completed = true;
      return;
    }
    this.index += 1;
    this.actionDone = false;
    this.rejectionKey = null;
  }

  attemptDiscard(seat: Seat, tileId: number): AttemptResult {
    const step = this.currentStep();
    const exp = step.expectedAction;
    if (exp.kind !== 'discard') return this.reject('lesson.wrongAction');
    if (this.state.phase !== 'awaiting-discard' || this.state.turn !== seat) {
      return this.reject('lesson.notYourTurn');
    }
    if (!(this.state.hands[seat] ?? []).some((t) => t.id === tileId)) {
      return this.reject('lesson.illegalMove');
    }
    if (exp.tileId !== undefined && exp.tileId !== tileId) return this.reject('lesson.wrongTile');
    this.state = discardTile(this.state, seat, tileId);
    return this.onActionApplied(step);
  }

  attemptTsumo(seat: Seat): AttemptResult {
    const step = this.currentStep();
    const s = this.state;
    if (step.expectedAction.kind !== 'tsumo') return this.reject('lesson.wrongAction');
    if (s.phase !== 'awaiting-discard' || s.turn !== seat || !canTsumo(s)) {
      return this.reject('lesson.cannotTsumo');
    }
    this.state = declareTsumo(s, seat);
    return this.onActionApplied(step);
  }

  attemptClaim(seat: Seat, kind: ClaimKind, option?: ChiOption): AttemptResult {
    const step = this.currentStep();
    const exp = step.expectedAction;
    if (exp.kind !== 'claim' || exp.claim !== kind) return this.reject('lesson.wrongAction');
    if (this.state.phase !== 'claim-window') return this.reject('lesson.notYourTurn');
    const seatClaims = claimsFor(this.state).find((c) => c.seat === seat);
    const claim = seatClaims === undefined ? null : buildClaim(seat, kind, seatClaims, option);
    if (claim === null) return this.reject('lesson.cannotClaim');
    this.resolveClaims([...this.pendingBotClaims, claim]);
    this.pendingBotClaims = [];
    return this.onActionApplied(step);
  }

  /** Decline the open claim window (only when the step is not asking for a call). */
  attemptPass(): AttemptResult {
    const step = this.currentStep();
    if (step.expectedAction.kind === 'claim') return this.reject('lesson.shouldClaim');
    if (this.state.phase !== 'claim-window') return this.reject('lesson.wrongAction');
    this.resolveClaims(this.pendingBotClaims);
    this.pendingBotClaims = [];
    this.settleBots();
    return { ok: true };
  }

  private onActionApplied(step: LessonStep): AttemptResult {
    this.actionDone = true;
    this.rejectionKey = null;
    this.settleBots();
    if (step.autoAdvance) this.advance();
    return { ok: true };
  }

  private reject(reasonKey: string): AttemptResult {
    this.rejectionKey = reasonKey;
    return { ok: false, reasonKey };
  }

  /** Run bots + auto-resolve claims until the human must decide or the hand
   *  ends, stashing bot calls at any window the local seat can act in. */
  private settleBots(): void {
    for (;;) {
      const s = this.state;
      if (s.phase === 'hand-end') return;
      if (s.phase === 'awaiting-discard') {
        if (s.turn === LOCAL_SEAT) return;
        this.runBotTurn(s.turn);
        continue;
      }
      const bots: Claim[] = [];
      let localCanClaim = false;
      for (const c of claimsFor(s)) {
        if (c.seat === LOCAL_SEAT) {
          localCanClaim = true;
          continue;
        }
        const choice = this.strategy.chooseClaim(c.seat, s, c);
        if (choice !== null) bots.push(choice);
      }
      if (localCanClaim) {
        this.pendingBotClaims = bots;
        return;
      }
      this.resolveClaims(bots);
    }
  }

  private runBotTurn(seat: Seat): void {
    const s = this.state;
    const hand = s.hands[seat] ?? [];
    const meldCount = (s.melds[seat] ?? []).length;
    this.state =
      canTsumo(s) && this.strategy.chooseTsumo(hand, meldCount)
        ? declareTsumo(s, seat)
        : discardTile(s, seat, this.strategy.chooseDiscard(hand, this.rng).id);
  }

  private resolveClaims(claims: readonly Claim[]): void {
    const s = this.state;
    if (s.phase !== 'claim-window' || s.lastDiscard === null) return;
    const winner = resolveClaimPriority(claims, s.lastDiscard.from);
    this.state = winner === null ? passClaims(s) : applyWinner(s, winner);
  }
}
